import useClientCachedURL from "../hooks/useClientCachedURL";
import useStoreMetaOfferWhenLanding from "../hooks/useStoreLandingMetaOffer";
import React, { FC, Profiler, useCallback, useEffect, useMemo, useContext } from "react";
import { Helmet } from "react-helmet-async";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  metaPreOptedAncillariesFromFlightDetailsScreenQueryToApiQuery,
  metaPreOptedAncillariesFromFlightToStore
} from "../utils/preOptedAncillaries";
import { flightsSessionStore } from "@flights/web-api-utils-universal";
import { trackMetaChannelStagesV2, trackMetaLandingsCustomGoalsV2 } from "../utils/experiments/track-meta-landings-v2";
import { isOfGoogleFlightsOrigin, isOfMetaOrigin, isOfSkyScannerOrigin } from "../utils/marketing-url-params";
import { getMarketingTrackingVariable } from "@flights/web-api-utils-universal";
import { UIFlightData, UIFlightSegment, UIServiceError } from "@flights/types";
import AppShell from "../components/elements/AppShell";
import { FlightDetailsInner } from "../components/elements/FlightDetailsInner";
import { POSMisMatch } from "../components/elements/POSMisMatch";
import {
  FLIGHT_DETAILS_RETRYABLE_ERROR_CODES,
  FLIGHT_DETAILS_MODAL_CLOSE_ERROR_CODES,
  LANDING_OFFER_FROM_META_KEY,
  PRICE_ACCURACY_TAG_FIRED_OFFER,
  SEATMAP_EXTRA_TYPE
} from "../constants";
import { useClientFetch } from "../hooks/useClientFetch";
import useClientMetrics from "../hooks/useClientMetrics";
import useCreateUrl from "../hooks/useCreateUrl";
import useEventTracking from "../hooks/useEventTracking";
import useFlightDetailsFromSearchResults from "../hooks/useFlightDetailsFromSearchResults";
import useGlobalContext from "../hooks/useGlobalContext";
import { useGoogleFlightsPriceAccuracyTag, useSkyScannerPriceAccuracyTag } from "../hooks/useGTMPriceAccuracyTags";
import useTrackPageLoad from "../hooks/useTrackPageLoad";
import { actions as extraProductActions } from "../ancillaries/store/extraProducts/actions";
import { actions as flightDetailsActions } from "../store/flightDetails/actions";
// eslint-disable-next-line import/no-deprecated
import { useActions, useStore } from "../store";
import useTrackFlightDetailsPriceChangeGoal from "../hooks/useTrackFlightDetailsPriceChangeGoal";
import StaticContext from "../app/StaticContext/StaticContext";
import useReportPriceDiscrepancy from "../hooks/useReportPriceDiscrepancy";
import useTrackFlightsWebUseOfferPos from "../hooks/useTrackFlightsWebUseOfferPosExp";
import useRemoveExternalParamsFromUrl from "../hooks/useRemoveExternalParamsFromUrl";
import useRetryFlightDetailsApi from "../hooks/useRetryFlightDetailsApi";
import useFlightOfferInSearchResults from "../hooks/useFlightOfferInSearchResults";
import useLogFORAPErrors from "../hooks/useLogFORAPErrors";
import { trackAppsShareFlightWebAAStages } from "../utils/experiments/meta/flights_ios_share_flight_link";
import useTrackFlightDetailsBrandedFaresGoals from "../hooks/useTrackFlightDetailsBrandedFaresGoals";
import flights_web_apex_ancillaries_aa from "../utils/experiments/apex/flights_web_apex_ancillaries_aa";
import useUserAgent from "../hooks/useUserAgent";
import { usePrefetchedBrandedFaresOffer, useSearchResultOffer } from "../hooks/useSearchResults";
import useBrandedFares from "../branded-fares/hooks/useBrandedFares";
import useAlternativeFaresParams from "../hooks/useAlternativeFaresParams";
import flights_mdotvalue_sr_retain_scroll_on_close_flight_details from "../utils/experiments/mdotvalue/flights_mdotvalue_sr_retain_scroll_on_close_flight_details";
import { carrierUtils, pricingUtils } from "@flights/web-api-utils-universal";
import useKayakS2SParams from "../hooks/useKayakS2SParams";
import usePrefetchFlightDetails from "../hooks/usePrefetchFlightDetails";
import useGetSearchResultsBound from "../hooks/useGetSearchResultsBound";
import { goal, trackWithDefaultStage } from "@flights/et-universal";
import useProfiler from "../hooks/useProfiler";
import { WishlistWidgetLoaderMobile } from "../components/remote";
import useCaParamsCleanup from "../hooks/useCaParamsCleanup";
import useEvents from "../hooks/useEvents";

const debug = require("debug")("screen:FlightDetails");

const FlightDetails = () => {
  const staticContext = useContext(StaticContext);
  const location = useLocation();
  const params = useParams<{ airports: string; id: string; outboundToken?: string }>();
  /* eslint-disable import/no-deprecated */
  const store = useStore();
  const actions = useActions(flightDetailsActions);
  const extrasActions = useActions(extraProductActions);
  /* eslint-enable import/no-deprecated */
  const history = useHistory();
  const queryParams = new URLSearchParams(location.search);
  const trackContextEventV2 = useTrackPageLoad("flight_details");
  const { createUrl } = useCreateUrl();
  const { requestId, features } = useGlobalContext();
  const trackV2 = useEventTracking("flight_details", requestId);
  const googleFlightsPriceAccuracyTag = useGoogleFlightsPriceAccuracyTag();
  const skyScannerPriceAccuracyTag = useSkyScannerPriceAccuracyTag();
  const { trackRenderTime } = useClientMetrics();
  const { cacheLastSuccessfulSearchUrl } = useClientCachedURL();
  const isMetaUser = isOfMetaOrigin();
  const isGoogleFlightsUser = isOfGoogleFlightsOrigin();
  const isSkyscannerUser = isOfSkyScannerOrigin();
  const enableGfsPosMismatch = features["FLIGHTS_META_ENABLE_GFS_FOR_POS_MISMATCH"] && isGoogleFlightsUser;
  const flightsWebUseOfferPos = useTrackFlightsWebUseOfferPos();
  const { setFlightDetailsApiForRetry } = useRetryFlightDetailsApi();
  const { getTotalPrice } = useFlightOfferInSearchResults();
  const { flight: fetchedFlight, fetchStatus } = store.flightDetails;
  const { isLoggedIn } = useProfiler();
  const { grumble } = useEvents();

  const { getFetchedFlightDetails } = usePrefetchFlightDetails(); // flights_web_prefetch_flight_details
  const prefetchedFlight = getFetchedFlightDetails(params?.id); // flights_web_prefetch_flight_details
  const cleanupCaParams = useCaParamsCleanup();

  if (process.env.BUILD_TARGET === "server" && staticContext.set) {
    const { getInitialState } = require("../server/screens/search");
    staticContext.set({ getInitialState: getInitialState(queryParams) });
  }

  useRemoveExternalParamsFromUrl();
  useStoreMetaOfferWhenLanding(params?.id);

  const { isMobile } = useUserAgent();

  const landingOfferFromMeta = flightsSessionStore.get(LANDING_OFFER_FROM_META_KEY);
  useEffect(() => {
    trackContextEventV2();
    goal("flights_flight_details_page_view");
    cacheLastSuccessfulSearchUrl(params?.airports);
    trackMetaChannelStagesV2("mdot");
    flights_web_apex_ancillaries_aa.stages.flight_details();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { setAlternativeFaresParams, getAlternativeFaresParams } = useAlternativeFaresParams();

  useEffect(() => {
    const params = getAlternativeFaresParams();
    if (isMetaUser && !params?.searchedAt) {
      setAlternativeFaresParams("searchedAt", new Date().getTime());
    }
  }, [getAlternativeFaresParams, isMetaUser, setAlternativeFaresParams]);

  let flight = useFlightDetailsFromSearchResults(params?.id);

  useEffect(() => {
    if (flight) {
      const queryParams = new URLSearchParams(location.search);
      trackAppsShareFlightWebAAStages(true, queryParams.get("label") || undefined);
    }
  }, [flight, isMetaUser, location.search]);

  useLogFORAPErrors(params.id);
  const urlParams = new URLSearchParams(useLocation().search);

  const searchParams = metaPreOptedAncillariesFromFlightDetailsScreenQueryToApiQuery(urlParams);

  useKayakS2SParams(fetchStatus, flight, landingOfferFromMeta, searchParams, params);

  searchParams.set("excludedAncillaries", SEATMAP_EXTRA_TYPE);

  if (flightsWebUseOfferPos.isVariant) {
    const salesCountry = getMarketingTrackingVariable("salesCountry");
    if (salesCountry) {
      searchParams.set("salesCountry", salesCountry);
      setAlternativeFaresParams("salesCountry", salesCountry);
    }
  }

  const flightOffer = useSearchResultOffer(params?.id);
  const prefetchedbrandedFareOffer = usePrefetchedBrandedFaresOffer(params?.id);
  const brandedFareOffer = useBrandedFares(params?.id).brandedFareOffers?.length;

  const hasPrefetchedBrandedFareOffers =
    flightOffer && !flightOffer.requestableBrandedFares && !prefetchedbrandedFareOffer && brandedFareOffer;

  if (hasPrefetchedBrandedFareOffers || prefetchedbrandedFareOffer) {
    searchParams.set("hasPrefetchedBrandedFareOffers", "true");
  }

  const hasFlightInStore = useMemo(
    () => store.searchResults.flights.find((flight) => flight.token === params.id),
    [store.searchResults, params.id]
  );

  const requestableBrandedFares = hasFlightInStore?.requestableBrandedFares;
  const shouldSkipBrandedFares = hasFlightInStore && !requestableBrandedFares;

  if (shouldSkipBrandedFares) {
    searchParams.set("skipBrandedFares", "1");
  }

  const { units, nanos, currencyCode } = getTotalPrice(params?.id, urlParams.get("baseOfferToken") || "") || {};
  if (units) {
    const formattedPrice = `${units}-${nanos}-${currencyCode}`;
    setAlternativeFaresParams("priceInSearch", formattedPrice);
  } else {
    setAlternativeFaresParams("priceInSearch", "");
  }

  const brandedFareIndex = urlParams.get("brandedFareIndex");
  const caSource = urlParams.get("ca_source");
  const caPrice = urlParams.get("ca_price");
  const source = urlParams.get("source");

  if (caSource) {
    searchParams.set("ca_source", caSource);
  }

  if (caPrice) {
    searchParams.set("ca_price", caPrice);
  }

  if (brandedFareIndex) {
    searchParams.set("brandedFareIndex", brandedFareIndex);
  }

  if (source) {
    searchParams.set("source", source);
  }

  const url = `/api/flight/${params?.id}?${searchParams.toString()}`;

  useClientFetch(url, {
    fetch: () => {
      actions.fetch();
    },
    condition: () => !prefetchedFlight, // flights_web_prefetch_flight_details
    success: (data: UIFlightData) => {
      onFetchSuccess(data);
    },

    error: (error: UIServiceError) => {
      grumble("Failed to fetch flight details on mobile", error);

      if (FLIGHT_DETAILS_MODAL_CLOSE_ERROR_CODES.includes(error.code)) {
        backToSearch({ forceReload: false });
        return;
      }

      if (isGoogleFlightsUser) {
        trackV2("google_flights_landing_shallow", {
          isShallow: true,
          airlineIATA: []
        });
        googleFlightsPriceAccuracyTag(0, "");
      }

      if (flight) {
        setFlightDetailsApiForRetry(error);
      }

      actions.fetchError(error);
    }
  });

  const onFetchSuccess = (data: UIFlightData) => {
    actions.fetchSuccess(data);
    data.ancillaries && metaPreOptedAncillariesFromFlightToStore(data.ancillaries, extrasActions);

    const totalPrice = pricingUtils.priceToNumber(data.priceBreakdown.total);
    if (params?.id === landingOfferFromMeta && isGoogleFlightsUser) {
      trackV2("google_flights_landing_shallow", {
        isShallow: false,
        airlineIATA: carrierUtils.uniqueCarriersFromSegments(data.segments)
      });
      googleFlightsPriceAccuracyTag(totalPrice, data.priceBreakdown.total.currencyCode);
    }
    if (
      params?.id === landingOfferFromMeta &&
      flightsSessionStore.get(PRICE_ACCURACY_TAG_FIRED_OFFER) !== params?.id &&
      isSkyscannerUser
    ) {
      skyScannerPriceAccuracyTag(totalPrice, data.priceBreakdown.total.currencyCode);
      flightsSessionStore.set(PRICE_ACCURACY_TAG_FIRED_OFFER, params?.id);
    }
  };

  useEffect(() => {
    // flights_web_prefetch_flight_details
    if (prefetchedFlight) onFetchSuccess(prefetchedFlight);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      cleanupCaParams();
    };
  }, [cleanupCaParams]);

  // flights_web_search_cache_ttl_30_mins
  useTrackFlightDetailsPriceChangeGoal();
  useTrackFlightDetailsBrandedFaresGoals();

  type CloseParams = {
    forceReload: boolean | undefined;
  };

  const bound = useGetSearchResultsBound();

  const backToSearch = useCallback(
    (p?: CloseParams) => {
      actions.fetch(); //reset the current flight in store
      const urlParams = new URLSearchParams(location.search);
      urlParams.delete("selectedProducts");
      urlParams.delete("brandedFareIndex");

      trackMetaLandingsCustomGoalsV2(1, "mdot");

      // START - flights_mdotvalue_sr_retain_scroll_on_close_flight_details
      const { fromSearch } = (location.state as { fromSearch: boolean }) || {};
      const isRetainScrollOnCloseExperimentVariant =
        flights_mdotvalue_sr_retain_scroll_on_close_flight_details.isVariant;
      const shouldUseHistoryBack: boolean =
        fromSearch && !p?.forceReload && isMobile && isRetainScrollOnCloseExperimentVariant();
      if (shouldUseHistoryBack) {
        history.goBack();
      } else {
        // END - flights_mdotvalue_sr_retain_scroll_on_close_flight_details
        let url = `/flights/${params?.airports}?${urlParams.toString()}`;

        if (bound === "outbound") {
          url = `/flights/${params?.airports}/outbound/?${urlParams.toString()}`;
        }

        if (bound === "inbound" && params.outboundToken) {
          url = `/flights/${params?.airports}/inbound/${params.outboundToken}?${urlParams.toString()}`;
        }

        history.push(createUrl(url), {
          forceRefetch: p?.forceReload
        });
      }
    },
    [
      actions,
      location.search,
      location.state,
      isMobile,
      history,
      params?.airports,
      params.outboundToken,
      bound,
      createUrl
    ]
  );

  const handleErrorClose = () => {
    const errorDoesNotNeedReload = FLIGHT_DETAILS_RETRYABLE_ERROR_CODES.includes(
      (store.flightDetails.error as UIServiceError)?.code || ""
    );

    backToSearch({ forceReload: !errorDoesNotNeedReload });
  };

  useReportPriceDiscrepancy(params.id);

  if (fetchStatus === "success" && fetchedFlight) {
    debug("FlightDetails fetched", fetchedFlight);
    flight = fetchedFlight;
  }

  const showPOSMismatch = flight && flight.posMismatch?.isPOSMismatch && !enableGfsPosMismatch;

  return (
    <Profiler id="screen" onRender={trackRenderTime}>
      {flight && <PageHeader segments={flight.segments} />}
      <AppShell noPadding>
        {fetchStatus === "success" && showPOSMismatch && flight?.posMismatch?.isPOSMismatch && (
          <POSMisMatch goToSearch={backToSearch} POSMismatch={flight.posMismatch} />
        )}
        {(fetchStatus === "loading" || fetchStatus === "failed" || (flight && !showPOSMismatch)) && (
          <FlightDetailsInner
            flightToken={params?.id}
            flight={flight}
            fetchStatus={fetchStatus}
            onClose={backToSearch}
            onErrorClose={handleErrorClose}
            hasPrefetchedBrandedFareOffers={!!(hasPrefetchedBrandedFareOffers || prefetchedbrandedFareOffer)}
          />
        )}
        {isLoggedIn() && !!trackWithDefaultStage("ddot_wl_save_flights", 3) ? <WishlistWidgetLoaderMobile /> : null}
      </AppShell>
    </Profiler>
  );
};

export const PageHeader: FC<{ segments: UIFlightSegment[]; children?: React.ReactNode }> = ({ segments, children }) => {
  const title = useMemo(() => {
    return segments
      .map(({ departureAirport, arrivalAirport }) => `${departureAirport.cityName} – ${arrivalAirport.cityName}`)
      .join(" | ");
  }, [segments]);

  return (
    <Helmet>
      <title>{title} - Booking.com</title>
      <meta property="webview:header" content="regular" />
      <meta property="webview:title" content={""} />
      {children}
    </Helmet>
  );
};

export default FlightDetails;
